var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { MultiSelectBase } from '../MultiSelectBase';
/**
 * PagedMultiSelectFilter
 * Multi-select popup filter that loads options asynchronously via `loadOptions`.
 * Supports search (re-fetches from start) and infinite scroll (appends next page).
 * `loadSelectedValues` resolves labels for selected ids that are not on the current page
 * (IP-15815 persist + reload).
 * Parity with legacy `PagedMultiSelect.jsx`.
 * @version 1.1.0
 * @since 2026-05-04
 */
export function PagedMultiSelectFilter({ label, value, onChange, loadOptions, loadSelectedValues, placeholder, disabled, showReset }) {
    const [pageOptions, setPageOptions] = useState([]);
    const [selectedExtras, setSelectedExtras] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const isLoadingRef = useRef(false);
    const cursorRef = useRef(undefined);
    const queryRef = useRef('');
    const exhaustedRef = useRef(false);
    const requestIdRef = useRef(0);
    const selectedExtrasRef = useRef([]);
    selectedExtrasRef.current = selectedExtras;
    const fetchOptions = useCallback((query, cursor) => __awaiter(this, void 0, void 0, function* () {
        if (cursor && isLoadingRef.current)
            return;
        const requestId = cursor ? requestIdRef.current : requestIdRef.current + 1;
        if (!cursor)
            requestIdRef.current = requestId;
        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            const result = yield loadOptions(query, cursor);
            if (requestId !== requestIdRef.current)
                return;
            setPageOptions((prev) => (cursor ? [...prev, ...result.options] : result.options));
            cursorRef.current = result.nextCursor;
            exhaustedRef.current = !result.nextCursor;
        }
        finally {
            if (requestId === requestIdRef.current) {
                isLoadingRef.current = false;
                setIsLoading(false);
            }
        }
    }), [loadOptions]);
    useEffect(() => {
        void fetchOptions('', undefined);
    }, [fetchOptions]);
    useEffect(() => {
        const selected = new Set(value);
        const onPage = new Set(pageOptions.map((option) => option.value));
        setSelectedExtras((prev) => {
            const next = prev.filter((option) => selected.has(option.value) && !onPage.has(option.value));
            if (next.length === prev.length && next.every((option, index) => option.value === prev[index].value)) {
                return prev;
            }
            return next;
        });
        if (!loadSelectedValues || value.length === 0)
            return undefined;
        const known = new Set([
            ...onPage,
            ...selectedExtrasRef.current.filter((option) => selected.has(option.value)).map((option) => option.value)
        ]);
        const missing = value.filter((id) => !known.has(id));
        if (missing.length === 0)
            return undefined;
        let cancelled = false;
        void loadSelectedValues(missing)
            .then((resolved) => {
            if (cancelled || resolved.length === 0)
                return;
            setSelectedExtras((prev) => {
                const have = new Set(prev.map((option) => option.value));
                const extras = resolved.filter((option) => selected.has(option.value) && !onPage.has(option.value) && !have.has(option.value));
                return extras.length ? [...prev, ...extras] : prev;
            });
        })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [value, pageOptions, loadSelectedValues]);
    const options = useMemo(() => {
        if (selectedExtras.length === 0)
            return pageOptions;
        const have = new Set(pageOptions.map((option) => option.value));
        return [...pageOptions, ...selectedExtras.filter((option) => !have.has(option.value))];
    }, [pageOptions, selectedExtras]);
    const handleSearch = useCallback((query) => {
        queryRef.current = query;
        cursorRef.current = undefined;
        exhaustedRef.current = false;
        void fetchOptions(query, undefined);
    }, [fetchOptions]);
    const handleEndReached = useCallback(() => {
        if (exhaustedRef.current || isLoading)
            return;
        void fetchOptions(queryRef.current, cursorRef.current);
    }, [fetchOptions, isLoading]);
    return (React.createElement(MultiSelectBase, { label: label, value: value, onChange: onChange, options: options, placeholder: placeholder, disabled: disabled, showReset: showReset, isLoading: isLoading, onSearch: handleSearch, onEndReached: handleEndReached }));
}
